import { notification, message } from 'antd';
import { ExclamationCircleTwoTone } from '@ant-design/icons';
import React from 'react';
import { debounce, put, takeEvery, type Effect, type ForkEffect, select } from 'redux-saga/effects';
import * as Effects from 'redux-saga/effects';
import { push, replace } from 'redux-first-history';
import { type PayloadAction } from '@reduxjs/toolkit';

import { actions as auth } from '~/redux/auth/slice';

import { type ColumnDefinition, getColumnFromKey } from '~/types/components';
import type { ErrorResponse } from '~/types/responses';

import * as Api from '~/api';
import { actions } from './slice';

const call: any = Effects.call;

export function* watchFetchBackendVersion(): Generator<Effect, void, any> {
  let backendVersion;
  try
  {
    backendVersion = yield call(Api.fetchBackendVersion);
  } catch (e) {
    Api.handleError(e);
    return;
  }

  const frontendVersion = yield select((state) => state.app.version);

  if (String(backendVersion) !== String(frontendVersion)) {
    notification.open({
      message: 'Versions incompatibles',
      description: (
        <div>
          Les versions Backend ({backendVersion}) et Frontend ({frontendVersion}) de l&apos;application sont
          incompatibles.
          <br />
          <a
            href='#'
            onClick={() => {
              document.location.reload();
            }}
          >
            <strong>Cliquez ici pour rafraîchir votre page.</strong>
          </a>
        </div>
      ),
      duration: null,
      icon: <ExclamationCircleTwoTone twoToneColor='red' />,
    });
  }

  yield put(actions.setBackendVersion(backendVersion));
}

export function* watchGoto({ payload }: PayloadAction<any>): Generator<Effect, void, any> {
  const documentationRunning = yield select((state) => state.app.documentationRunning);
  if (documentationRunning) {
    return;
  }

  const blockNavigation = yield select((state) => state.app.blockNavigation);
  const blockState = yield select((state) => state.app.blockState);

  if (blockNavigation) {
    switch (blockState) {
      case 'blocked':
        return;
      case 'unblocked':
        yield put(actions.blockTarget(payload));
        yield put(actions.blockState('blocked'));
        return;
      case 'proceeding':
        yield put(actions.blockNavigation(false));
        yield put(actions.blockState('unblocked'));
        yield put(actions.blockTarget(''));
        break;
    }
  }

  yield put(push(payload));
}

function buildDescriptionFromErrors(
  errors: Record<string, string[]> | undefined,
  columns: Array<ColumnDefinition<any>>,
): React.ReactElement {
  if (!errors) {
    return <span></span>;
  }
  return (
    <dl>
      {Object.entries(errors).map((tuple, index) => {
        let label = tuple[0];
        if (label === 'id') {
          return <React.Fragment key={index}></React.Fragment>;
        }
        const column = getColumnFromKey(columns, tuple[0]);
        if (column) {
          label = column.title;
        }
        return (
          <span key={index}>
            <dt>{label}</dt>
            {tuple[1].map((v, i) => (
              <dd key={i}>{v}</dd>
            ))}
          </span>
        );
      })}
    </dl>
  );
}

export function* watchHandleError(
  error: ErrorResponse,
  columns?: Array<ColumnDefinition<any>>,
): Generator<Effect, void> {
  switch (error.status) {
    case 403:
      message.error(error.message, 10);
      break;
    case 404:
      message.error(error.message, 10);
      yield put(replace('/'));
      break;
    case 422:
      notification.open({
        message: error.message,
        description: buildDescriptionFromErrors(error.errors, columns ?? []),
        duration: null,
        icon: <ExclamationCircleTwoTone twoToneColor='red' />,
      });
      break;
    case 401:
      message.error(error.message, 10);
      yield put(auth.setUser(null));
      yield put(push('/'));
      break;
  }
}

export function* watchAppSagas(): Generator<ForkEffect, void> {
  yield debounce(500, actions.fetchBackendVersion, watchFetchBackendVersion);
  yield takeEvery(actions.goto, watchGoto);
}

const appSagas = watchAppSagas;

export default appSagas;
